// The creator on a title page (review 2026-10-19 #3): who made it, shown by what they made. Their other games as a
// small art mosaic and their name. No letter-avatar slab, and for now no profile avatar either (a hotlinked image;
// see parseProfile in zones/creators/follow.ts). An unclaimed creator with no other game gets no
// card at all (the hero's "by …" line already names them).

import type { Catalogue, Game } from "../../catalogue.ts";
import { creatorName, lazyCover, typedCover } from "../../components.ts";
import { h, link } from "../../dom.ts";
import { coverUrl } from "../../rows.ts";

/** Their other listed games, real art first, then typographic covers; at most `n`. */
export function otherGames(cat: Pick<Catalogue, "games">, g: Pick<Game, "id" | "creator">, n = 3): Game[] {
  const rank = (x: Game) => (!coverUrl(x) ? 2 : typedCover(x) ? 1 : 0);
  return cat.games
    .filter((x) => x.creator.id === g.creator.id && x.id !== g.id && !x.firstParty)
    .map((x, i) => ({ x, i }))
    .sort((a, b) => rank(a.x) - rank(b.x) || a.i - b.i)
    .slice(0, n)
    .map((r) => r.x);
}

/** Whether the card shows: a claimed creator always, anyone else only when they have something more to show. */
export function showCreatorCard(g: Pick<Game, "creator">, others: readonly Game[]): boolean {
  return !!g.creator.claimedBy || others.length > 0;
}

export function creatorCard(g: Game, cat: Pick<Catalogue, "games">, extra: HTMLElement | null): HTMLElement | null {
  const others = otherGames(cat, g);
  if (!showCreatorCard(g, others)) return null;
  const name = creatorName(g.creator.name);
  const all = cat.games.filter((x) => x.creator.id === g.creator.id && !x.firstParty).length;
  const sub = [g.creator.claimedBy ? "Verified creator" : null, all > 1 ? `${all} games on Banger` : null].filter(Boolean).join(" · ");
  return h(
    "section",
    { class: "side-box gt-creator" },
    h("h2", null, "Creator"),
    link(
      `/c/${g.creator.id}`,
      { class: "gt-creator-link", "aria-label": `${name}: see everything they made` },
      others.length
        ? h("span", { class: "gt-creator-mosaic", "data-n": String(others.length), "aria-hidden": "true" }, others.map((x) => h("span", { class: typedCover(x) ? "typed" : null, ...lazyCover(x, "thumb") })))
        : null,
      h("span", { class: "gt-creator-name" }, h("span", null, name, sub ? h("span", { class: "gt-creator-sub" }, sub) : null)),
    ),
    extra,
  );
}
